import { TS_REGEX } from './constants';
import {
  parseMs,
  cleanLyricText,
  isInstrumentalLyric,
  detectSpacingPreference
} from './parser';
import type { ConversionOptions, ConversionResult } from './types';

interface TimestampItem {
  ts: string;
  ms: number;
}

interface MergedEntry {
  ms: number;
  text: string;
}

/**
 * Converts an unmerged LRC file (with duplicate lines repeated at different timestamps)
 * into a merged LRC format where identical lines are grouped into multi-timestamp lines
 * sorted chronologically by their first occurrence.
 *
 * @param rawText Raw LRC string.
 * @param options Optional conversion configuration.
 * @returns Structured ConversionResult with output text and metadata metrics.
 */
export function merge(
  rawText: string,
  options: ConversionOptions = {}
): ConversionResult {
  const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now();

  const {
    blankHandling = 'merge',
    blankSymbol = '',
    normalizeSpaces = true,
    spacing = 'none'
  } = options;

  const lines = rawText.split(/\r?\n/);
  const headers: string[] = [];
  const groups = new Map<string, TimestampItem[]>();
  const separateBlanks: MergedEntry[] = [];
  const spacer = detectSpacingPreference(lines, spacing);
  let blankCount = 0;

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    const matches = line.match(TS_REGEX);
    if (!matches) {
      headers.push(line);
      continue;
    }

    let lyric = cleanLyricText(line.replace(TS_REGEX, ''), normalizeSpaces);
    const isBlank = isInstrumentalLyric(lyric, blankSymbol);

    if (isBlank) {
      blankCount += matches.length;
      if (blankHandling === 'remove') {
        continue;
      }
      if (blankHandling === 'keep') {
        const activeText = blankSymbol || lyric;
        const sym = activeText
          ? `${!spacer && spacing !== 'none' ? ' ' : spacer}${activeText}`
          : '';
        for (const ts of matches) {
          separateBlanks.push({
            ms: parseMs(ts),
            text: `${ts}${sym}`
          });
        }
        continue;
      }
      // 'merge' uses blankSymbol if provided, otherwise preserves existing instrumental text
      lyric = blankSymbol ? blankSymbol : lyric;
    }

    const key = lyric;
    let list = groups.get(key);
    if (!list) {
      list = [];
      groups.set(key, list);
    }

    for (const ts of matches) {
      const ms = parseMs(ts);
      if (!list.some((item) => item.ts === ts)) {
        list.push({ ts, ms });
      }
    }
  }

  const mergedItems: MergedEntry[] = [];

  for (const [lyric, tsList] of groups.entries()) {
    if (tsList.length === 0) continue;

    // Sort timestamps within the same lyric line chronologically
    tsList.sort((a, b) => a.ms - b.ms);
    const combinedTs = tsList.map((item) => item.ts).join('');

    let lineSpacer = spacer;
    if (
      lyric &&
      lyric === blankSymbol &&
      !lineSpacer &&
      spacing !== 'none'
    ) {
      lineSpacer = ' ';
    }

    const lineText = lyric ? `${combinedTs}${lineSpacer}${lyric}` : combinedTs;
    const firstTs = tsList[0];

    mergedItems.push({
      ms: firstTs ? firstTs.ms : 0,
      text: lineText
    });
  }

  if (blankHandling === 'keep' && separateBlanks.length > 0) {
    mergedItems.push(...separateBlanks);
  }

  // Sort all lyric lines chronologically by their earliest timestamp
  mergedItems.sort((a, b) => a.ms - b.ms);

  const outputLines = headers.concat(mergedItems.map((item) => item.text));
  const endTime = typeof performance !== 'undefined' ? performance.now() : Date.now();

  return {
    text: outputLines.join('\n'),
    inputLines: lines.filter((l) => l.trim()).length,
    outputLines: outputLines.length,
    blankCount,
    durationMs: Math.max(0, Math.round((endTime - startTime) * 100) / 100)
  };
}
